import React, { useCallback } from 'react'

import { HiMagnifyingGlassMinus, HiMagnifyingGlassPlus } from 'react-icons/hi2'

import IconButton from '@/components/common/IconButtonV2'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/common/Select'
import {
  VIEWER_MAX_SCALE,
  VIEWER_MIN_SCALE,
  VIEWER_SCALES,
} from '@/components/office/OfficeViewer/OfficeViewerZoomControls/scales'

const SCALE_EPSILON = 0.001

const findNextPresetScale = (scale: number) => {
  return VIEWER_SCALES.find(
    (presetScale) => presetScale > scale + SCALE_EPSILON,
  )
}

const findPreviousPresetScale = (scale: number) => {
  for (let index = VIEWER_SCALES.length - 1; index >= 0; index -= 1) {
    if (VIEWER_SCALES[index] < scale - SCALE_EPSILON) {
      return VIEWER_SCALES[index]
    }
  }
  return undefined
}

export interface OfficeViewerZoomControlsProps {
  onFitWidth?: () => void
  pageScale: number
  pageScaleMode?: 'fixed' | 'fit-width'
  setPageScale: (pageScale: number) => void
}

export const OfficeViewerZoomControls: React.FC<
  OfficeViewerZoomControlsProps
> = ({ onFitWidth, pageScale, pageScaleMode = 'fixed', setPageScale }) => {
  const scaleOptions = Array.from(new Set([...VIEWER_SCALES, pageScale])).sort(
    (left, right) => left - right,
  )

  const currentScaleLabel = `${Math.round(pageScale * 100)}%`
  const selectValue =
    pageScaleMode === 'fit-width' ? 'fit-width' : String(pageScale)

  const handleZoomIn = useCallback(() => {
    const nextScale = findNextPresetScale(pageScale)
    if (nextScale !== undefined) {
      setPageScale(nextScale)
    }
  }, [pageScale, setPageScale])

  const handleZoomOut = useCallback(() => {
    const nextScale = findPreviousPresetScale(pageScale)
    if (nextScale !== undefined) {
      setPageScale(nextScale)
    }
  }, [pageScale, setPageScale])

  const handleScaleChange = useCallback(
    (value: string) => {
      if (value === 'fit-width') {
        onFitWidth?.()
        return
      }
      const nextScale = Number(value)
      if (Number.isFinite(nextScale) && nextScale > 0) {
        setPageScale(nextScale)
      }
    },
    [onFitWidth, setPageScale],
  )

  return (
    <>
      <IconButton
        type="button"
        onClick={handleZoomOut}
        aria-label="Zoom out"
        tooltip="Zoom out"
        tooltipContentProps={{
          side: 'top',
        }}
        disabled={pageScale <= VIEWER_MIN_SCALE}
      >
        <HiMagnifyingGlassMinus
          size={18}
          className="text-primary-600 dark:text-primary-400 stroke-[0.5px]"
        />
      </IconButton>
      <Select value={selectValue} onValueChange={handleScaleChange}>
        <SelectTrigger
          className="h-7 w-[76px] rounded-md border-primary-300 bg-white px-2 py-1 text-sm shadow-sm hover:border-primary-300 dark:border-primary-700 dark:bg-primary-800 dark:hover:border-primary-700 sm:w-[88px] sm:px-3"
          onDoubleClick={onFitWidth}
        >
          <SelectValue>
            {pageScaleMode === 'fit-width' ? 'Auto' : currentScaleLabel}
          </SelectValue>
        </SelectTrigger>
        <SelectContent
          className="z-[120] min-w-[112px]"
          data-office-viewer-overlay
        >
          {onFitWidth ? <SelectItem value="fit-width">Auto</SelectItem> : null}
          {scaleOptions.map((scale) => (
            <SelectItem key={scale} value={String(scale)}>
              {`${Math.round(scale * 100)}%`}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <IconButton
        type="button"
        onClick={handleZoomIn}
        aria-label="Zoom in"
        tooltip="Zoom in"
        tooltipContentProps={{
          side: 'top',
        }}
        disabled={pageScale >= VIEWER_MAX_SCALE}
      >
        <HiMagnifyingGlassPlus
          size={18}
          className="text-primary-600 dark:text-primary-400 stroke-[0.5px]"
        />
      </IconButton>
    </>
  )
}
